import { ChangeEventHandler, FC, MouseEventHandler, useContext, useEffect, useState } from "react";
import { BsCheckCircle, BsExclamationTriangle, BsPencil } from "react-icons/bs";
import { FaCheck } from "react-icons/fa";
import Textarea from "../ui/Textarea";
import { GroupsContext } from "../../context/GroupsContext";
import Autocomplete from "../ui/Autocomplete";
import { AppContext } from "../../context/AppContext";
import useApi from "../../hooks/useApi";
import Avatar from "../ui/Avatar";
import { Acceptance } from "../../types/ApiTypes";

type Types = 'customer'|'unit'|'project'|'text'|'person';

interface Props {
    title:string;
    value:string;
    italic?:boolean;
    editable?:boolean;
    displayText?:string;
    type?: Types;
    customerId?: string;
    caution?:boolean
    onEditDone?: (newValue: string) => void;
    withAvatar?:string
    accepted?:Acceptance
}

interface IMutatedUnit {
    label: string,
    value: IMutatedUnitValue
}

interface IMutatedUnitValue {
    id: number,
    children:number[],
    owner:string
}

const SpaceDetailsInfoListItem: FC<Props> = (props) => {
    const [customerOptions, setCustomerOptions] = useState<{ label: string; value: { id: string; ownerId: string; }}[]>([]);
    const [usersOptions, setUsersOptions] = useState<{ label: string, value: string }[]>([]);
    const [projectOptions, setProjectOptions] = useState<{label: string, value: string}[]>([]);
    const [unitOptions,setUnitOptions] = useState<{ main: IMutatedUnit[], secondary: IMutatedUnit[]}>({
        main:[],
        secondary:[]
    });


    const [editMode, setEditMode] = useState(false);

    const [textValue, setTextValue] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState<{ label: string; value: { id: string; ownerId: string; }}|undefined>();
    const [selectedUser, setSelectedUser] = useState<{ label: string, value: string }|undefined>();
    const [selectedProject, setSelectedProject] = useState<{label:string, value:string}|undefined>();
    const [selectedUnit, setSelectedUnit] = useState<IMutatedUnit|undefined>();

    const {state: groupState} = useContext(GroupsContext);
    const {state: appState} = useContext(AppContext);
    const {fetchSiblingOrders, fetchOrdersForCustomer, fetchCompanyUnits} = useApi();

    if(props.editable && !props.type) throw new Error('SpaceDetailsInfoListItem set as editable without valid type');

    useEffect(() => {
        if(props.type === 'text') setTextValue(props.value);
        if(props.type === 'unit') {
            fetchCompanyUnits().then(response => {
                if(!response) return;
                const main = response.main.map(x => ({label: x.name, value: {id: x.id, children:response.secondary.filter(y => y.parent_unit!.id === x.id).map(y => y.id), owner: x.owner}}));
                const secondary = response.secondary.map(x => ({label: x.name, value: {id: x.id, children:response.secondary.filter(y => y.parent_unit!.id === x.id).map(y => y.id), owner: x.owner}}));
                const selected = response.main.find(x => x.name === props.value) || response.secondary.find(x => x.name === props.value);
                if(selected) setSelectedUnit({label: selected.name, value: {id: selected.id, children:response.secondary.filter(y => y.parent_unit!.id === selected.id).map(y => y.id), owner: selected.owner}});
                setUnitOptions({main,secondary});
            });
            setTextValue(props.value);
        }
        if(props.type === 'customer') {
            setCustomerOptions(groupState.customers.map(x => ({label: x.name, value: {id:x.accountid, ownerId: x.ownerid}})));
            const selected = groupState.customers.find(x => x.accountid === props.value);
            if(selected) setSelectedCustomer({label: selected.name, value: {id:selected.accountid, ownerId: selected.ownerid}});
            setTextValue(props.value)
        }
        if(props.type === 'person') {
            if(appState.user) {
                setUsersOptions(appState.user.map(x => ({label: x.displayName, value: x.id})));
                const selected = appState.user.find(x => x.displayName === props.value);
                if(selected) setSelectedUser({label: selected.displayName, value: selected.id});
                setTextValue(props.value);
            }
        }
        if(props.type === 'project') {
            if(props.value === '') {
                if(props.customerId) {
                    fetchOrdersForCustomer(props.customerId).then(res => {
                        if(res) setProjectOptions(res.map(x => ({label: x.orderNumber, value: x.orderNumber})));
                        setSelectedProject(undefined);
                    })
                } else throw new Error('CustomerId missing for undefined value');
            } else {
                fetchSiblingOrders(props.value).then(res => {
                    if(res) {
                        setProjectOptions(res.map(x => ({label: x.orderNumber, value: x.orderNumber})));
                        const selected = res.find(x => x.orderNumber === props.value);
                        if(selected) setSelectedProject({label: selected.orderNumber, value: selected.orderNumber});
                        else setSelectedProject(undefined);
                    }
                })
            }
            setTextValue(props.value);
        }
        if(!props.editable) setTextValue(props.value)
    },[props.type, props.value, props.editable, groupState.customers, appState.user, props.customerId, fetchOrdersForCustomer, fetchSiblingOrders])

    const onCustomerSelected = (type: { label: string, value: { id: string, ownerId: string }}) => {
        setSelectedCustomer(type);
        setTextValue(type.value.id);
    }

    const onUserSelected = (type: { label: string, value: string}) => {
        setSelectedUser(type);
        setTextValue(type.label);
    }

    const onProjectSelected = (type: { label: string, value: string}) => {
        setSelectedProject(type);
        setTextValue(type.label);
    }

    const onMainUnitChange = (type: { label: string, value: IMutatedUnitValue }) => {
        setSelectedUnit(type);
        setTextValue(type.label);
    }

    const onInput: ChangeEventHandler<HTMLTextAreaElement> = e => {
        setTextValue(e.target.value);
    }

    const onEditEnd: MouseEventHandler = () => {
        setEditMode(false);
        if(textValue === props.value) return;
        if(props.onEditDone) {
            if(props.type==='person') return props.onEditDone(selectedUser!.value);
            props.onEditDone(textValue);
        }
    }

    return (
        <li className={`px-4 py-3 border border-black/20 rounded-md gap-2 flex flex-col ${props.italic ? 'italic' : ''}`}>
            {!editMode && (
                <>
                    <b>{props.title}:</b>
                    <div className='flex items-center justify-between'>
                        <div className='flex items-center gap-2'>
                            {props.withAvatar && <Avatar seed={props.withAvatar}/>}
                            <span>{props.displayText ? props.displayText : textValue}</span>
                            {props.accepted === Acceptance.Accepted && <BsCheckCircle className='text-success-darker text-xl'/>}
                            {props.accepted === Acceptance.Declined && <BsExclamationTriangle className='text-error-brighter text-xl'/>}
                        </div>
                        <div className='flex gap-2 items-center'>
                            {props.caution && <BsExclamationTriangle className='text-error-brighter text-xl'/>}
                            {props.editable && <BsPencil className='inline text-primary cursor-pointer text-xl' onClick={() => setEditMode(true)}/>}
                        </div>
                    </div>
                </>
            )}
            {editMode && (
                <div className='flex gap-2 items-center'>
                    {props.type === 'text' && <Textarea id='description' value={textValue} onChange={onInput} name='description' label='description'/>}
                    {props.type === 'customer' && <Autocomplete<{ id:string,ownerId:string }> options={customerOptions} defaultValue={selectedCustomer} onChange={onCustomerSelected}/>}
                    {props.type === 'person' && <Autocomplete<string> options={usersOptions} defaultValue={selectedUser} onChange={onUserSelected}/>}
                    {props.type === 'project' && <Autocomplete<string> options={projectOptions} defaultValue={selectedProject} onChange={onProjectSelected}/>}
                    {props.type === 'unit' && (
                        <>
                            <Autocomplete<IMutatedUnitValue> options={unitOptions.main} defaultValue={selectedUnit} onChange={onMainUnitChange}/>
                            {selectedUnit?.value?.children && selectedUnit.value.children.length > 0 && (
                                <Autocomplete<IMutatedUnitValue> options={unitOptions.secondary} onChange={onMainUnitChange}/>
                            )}
                        </>
                    )}
                    <FaCheck className='inline text-success-darker cursor-pointer' onClick={onEditEnd}/>
                </div>
            )}
        </li>
    );
};

export default SpaceDetailsInfoListItem;